import { createStyles, Group, Stack, Textarea } from '@mantine/core';
import { Icon, Text, Title } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

const useStyles = createStyles((theme) => ({
	input: {
		padding: 0,
		borderRadius: 0,
		fontFamily:
			'InterDisplay,InterVariable,Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,"Open Sans","Helvetica Neue",sans-serif',
		fontSize: theme.fontSizes.xl,
		fontWeight: 600,
		lineHeight: theme.other.typography.lineHeight.title.xl,

		'&:focus': {
			boxShadow: 'none',
			borderColor: 'transparent',
			backgroundColor: 'transparent',
		},

		'&:disabled': {
			opacity: 1,
			backgroundColor: theme.other.getColor('fill/transparent/default'),
			color: theme.other.getColor('text/primary/default'),
			cursor: 'text',
		},
	},
}));

interface QuestionTitleProps {
	readOnly?: boolean;
	value: string;
	originalTitle?: string;
	onChange: (value: string) => void;
	onChangeDebounced: (value: string) => void;
}

export const QuestionTitle = observer(
	({
		readOnly,
		value,
		originalTitle,
		onChange,
		onChangeDebounced,
	}: QuestionTitleProps) => {
		const { classes } = useStyles();

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				const newValue = event.currentTarget.value;
				onChange(newValue);
				onChangeDebounced(newValue);
			},
			[onChange, onChangeDebounced]
		);

		if (readOnly) {
			return <Title size="xl">{value}</Title>;
		}

		let titleValidationError: string | null = null;
		if (!value.length) {
			titleValidationError = 'Title is required';
		}

		// Only apply 280 character limit if the original title was under 280 characters
		// This allows legacy titles to remain over 280 characters if they originally had them
		const originalTitleLength = originalTitle?.length ?? 0;
		const shouldApplyCharacterLimit = originalTitleLength <= 280;

		if (shouldApplyCharacterLimit && value.length > 280) {
			titleValidationError = 'Title must be less than 280 characters';
		}

		return (
			<Stack spacing="xs">
				<Textarea
					classNames={classes}
					data-testid="input-title"
					value={value}
					onChange={handleChange}
					placeholder="Untitled question"
					variant="unstyled"
					autosize
					autoFocus={!readOnly && value === ''}
					size="xl"
				/>
				{titleValidationError && (
					<Group spacing="2xs">
						<Icon name="alertCircle" color="icon/critical/default" />
						<Text size="sm" color="text/critical/default">
							{titleValidationError}
						</Text>
					</Group>
				)}
			</Stack>
		);
	}
);
